import React, { useState, useEffect } from 'react';
import { apiClient } from '../lib/api';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Award, Search, Download, FileText, Clock, Building2, Calendar,
  User, CheckCircle, Eye
} from 'lucide-react';

const CertificadosPage = () => {
  const [voluntarios, setVoluntarios] = useState([]);
  const [selectedVoluntario, setSelectedVoluntario] = useState(null);
  const [missoesConclidas, setMissoesConclidas] = useState([]);
  const [certificados, setCertificados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [certificadoPreview, setCertificadoPreview] = useState(null);

  useEffect(() => {
    fetchVoluntarios();
    fetchCertificados();
  }, []);

  const fetchVoluntarios = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/voluntarios');
      setVoluntarios(response.data);
    } catch (error) {
      toast.error('Erro ao carregar voluntários');
    } finally {
      setLoading(false);
    }
  };

  const fetchCertificados = async () => {
    try {
      const response = await apiClient.get('/certificados');
      setCertificados(response.data);
    } catch (error) {
      console.error('Erro ao carregar certificados:', error);
    }
  };

  const fetchMissoesConclidas = async (voluntarioId) => {
    try {
      const response = await apiClient.get(`/voluntarios/${voluntarioId}/missoes-concluidas`);
      setMissoesConclidas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar missões');
    }
  };

  const handleSelectVoluntario = async (voluntario) => {
    setSelectedVoluntario(voluntario);
    await fetchMissoesConclidas(voluntario.id);
    setIsDetailOpen(true);
  };

  const gerarCertificadoPDF = async (missao) => {
    try {
      // Obter dados do certificado da API
      const response = await apiClient.post(
        `/voluntarios/${selectedVoluntario.id}/missoes/${missao.missao_id}/gerar-certificado`
      );
      const dados = response.data;

      // Criar PDF
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      // Configurações
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;

      // Fundo decorativo
      doc.setFillColor(0, 102, 204);
      doc.rect(0, 0, pageWidth, 15, 'F');
      doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');

      // Bordas decorativas laterais
      doc.setDrawColor(0, 102, 204);
      doc.setLineWidth(3);
      doc.line(10, 20, 10, pageHeight - 20);
      doc.line(pageWidth - 10, 20, pageWidth - 10, pageHeight - 20);

      // Logo ENTRAJUDA (texto estilizado)
      doc.setFontSize(28);
      doc.setTextColor(0, 102, 204);
      doc.setFont('helvetica', 'bold');
      doc.text('ENTRAJUDA', pageWidth / 2, 35, { align: 'center' });

      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.text('Associação para o Apoio a Instituições de Solidariedade Social', pageWidth / 2, 43, { align: 'center' });

      // Título do Certificado
      doc.setFontSize(32);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('CERTIFICADO DE VOLUNTARIADO', pageWidth / 2, 65, { align: 'center' });

      // Linha decorativa
      doc.setDrawColor(0, 102, 204);
      doc.setLineWidth(1);
      doc.line(margin + 40, 72, pageWidth - margin - 40, 72);

      // Texto principal
      doc.setFontSize(14);
      doc.setTextColor(50, 50, 50);
      doc.setFont('helvetica', 'normal');
      doc.text('Certifica-se que', pageWidth / 2, 88, { align: 'center' });

      // Nome do voluntário
      doc.setFontSize(24);
      doc.setTextColor(0, 102, 204);
      doc.setFont('helvetica', 'bold');
      doc.text(dados.voluntario.nome, pageWidth / 2, 102, { align: 'center' });

      // Detalhes da missão
      doc.setFontSize(12);
      doc.setTextColor(50, 50, 50);
      doc.setFont('helvetica', 'normal');
      
      const textoMissao = `participou como voluntário(a) na missão "${dados.missao.titulo}"`;
      doc.text(textoMissao, pageWidth / 2, 118, { align: 'center' });

      // Instituição
      doc.setFontSize(11);
      doc.text(`na instituição ${dados.instituicao.nome}`, pageWidth / 2, 128, { align: 'center' });

      // Período e horas
      const dataInicio = dados.missao.data_inicio ? new Date(dados.missao.data_inicio).toLocaleDateString('pt-PT') : 'N/A';
      const dataFim = dados.missao.data_fim ? new Date(dados.missao.data_fim).toLocaleDateString('pt-PT') : 'N/A';
      doc.text(`Período: ${dataInicio} a ${dataFim}`, pageWidth / 2, 140, { align: 'center' });
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total de ${dados.horas_realizadas} horas de voluntariado`, pageWidth / 2, 152, { align: 'center' });

      // Assinatura digital
      const dataEmissao = new Date(dados.data_emissao).toLocaleDateString('pt-PT');
      
      // Linha de assinatura
      doc.setLineWidth(0.5);
      doc.setDrawColor(150, 150, 150);
      doc.line(pageWidth / 2 - 50, 175, pageWidth / 2 + 50, 175);

      // Nome do gestor (assinatura estilizada)
      doc.setFontSize(14);
      doc.setTextColor(0, 102, 204);
      doc.setFont('times', 'italic');
      doc.text(dados.gestor.nome, pageWidth / 2, 172, { align: 'center' });

      // Cargo
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.text(dados.gestor.cargo, pageWidth / 2, 182, { align: 'center' });

      // Data de emissão e ID do certificado
      doc.setFontSize(9);
      doc.text(`Emitido em: ${dataEmissao}`, margin + 20, pageHeight - 22);
      doc.text(`Certificado Nº: ${dados.certificado_id}`, pageWidth - margin - 20, pageHeight - 22, { align: 'right' });

      // Descarregar o PDF
      doc.save(`Certificado_${dados.voluntario.nome.replace(/\s/g, '_')}_${dados.certificado_id}.pdf`);

      toast.success('Certificado gerado com sucesso!');
      fetchCertificados();
      fetchMissoesConclidas(selectedVoluntario.id);
    } catch (error) {
      console.error('Erro ao gerar certificado:', error);
      toast.error('Erro ao gerar certificado');
    }
  };

  const visualizarCertificado = async (cert) => {
    setCertificadoPreview(cert);
    setIsPreviewOpen(true);
  };

  const filteredVoluntarios = voluntarios.filter(v =>
    v.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="certificados-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Certificados de Voluntariado</h1>
          <p className="text-slate-600 dark:text-slate-400">Emitir certificados para voluntários após missões concluídas</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Award className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Certificados Emitidos</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{certificados.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <User className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Voluntários</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{voluntarios.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Total Horas</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {certificados.reduce((acc, c) => acc + (c.horas_realizadas || 0), 0)}h
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Pesquisar voluntário por nome ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-voluntarios-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Voluntários List */}
      <Card>
        <CardHeader>
          <CardTitle>Voluntários</CardTitle>
          <CardDescription>Selecione um voluntário para emitir certificados</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin" />
            </div>
          ) : filteredVoluntarios.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum voluntário encontrado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredVoluntarios.map(vol => {
                const certCount = certificados.filter(c => c.voluntario_id === vol.id).length;
                return (
                  <div
                    key={vol.id}
                    className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                    onClick={() => handleSelectVoluntario(vol)}
                    data-testid={`voluntario-item-${vol.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#0066CC]/10 flex items-center justify-center">
                        <User className="w-6 h-6 text-[#0066CC]" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-slate-900 dark:text-white">{vol.nome}</h3>
                          {certCount > 0 && (
                            <Badge variant="outline" className="text-green-600">
                              {certCount} certificado{certCount > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{vol.email}</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      <Award className="w-4 h-4 mr-2" />
                      Emitir Certificado
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Certificados Recentes */}
      <Card>
        <CardHeader>
          <CardTitle>Certificados Recentes</CardTitle>
          <CardDescription>Últimos certificados emitidos</CardDescription>
        </CardHeader>
        <CardContent>
          {certificados.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum certificado emitido ainda</p>
            </div>
          ) : (
            <div className="space-y-3">
              {certificados.slice(0, 10).map(cert => (
                <div
                  key={cert.id}
                  className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <Award className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{cert.voluntario_nome}</p>
                      <p className="text-sm text-slate-500">{cert.missao_titulo}</p>
                      <p className="text-xs text-slate-400">{cert.instituicao_nome} • {cert.horas_realizadas}h</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline">{cert.id}</Badge>
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(cert.data_emissao).toLocaleDateString('pt-PT')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Missões do Voluntário */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Missões de {selectedVoluntario?.nome}</DialogTitle>
            <DialogDescription>
              Selecione uma missão concluída para emitir certificado
            </DialogDescription>
          </DialogHeader>
          
          {missoesConclidas?.missoes?.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma missão concluída encontrada</p>
            </div>
          ) : (
            <div className="space-y-3 mt-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <span className="text-sm font-medium">Total de missões concluídas:</span>
                <Badge>{missoesConclidas?.total_missoes_concluidas || 0}</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <span className="text-sm font-medium">Total de horas:</span>
                <Badge variant="outline">{missoesConclidas?.total_horas || 0}h</Badge>
              </div>
              
              {missoesConclidas?.missoes?.map(missao => (
                <div
                  key={missao.missao_id}
                  className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-slate-900 dark:text-white">{missao.titulo}</h4>
                      <p className="text-sm text-slate-500 mt-1">
                        <Building2 className="w-3 h-3 inline mr-1" />
                        {missao.instituicao_nome}
                      </p>
                      <p className="text-sm text-slate-500">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {missao.data_inicio ? new Date(missao.data_inicio).toLocaleDateString('pt-PT') : 'N/A'}
                        {' - '}
                        {missao.data_fim ? new Date(missao.data_fim).toLocaleDateString('pt-PT') : 'N/A'}
                      </p>
                      <p className="text-sm text-slate-500">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {missao.horas_realizadas}h realizadas
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      {missao.certificado_emitido ? (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Certificado Emitido
                        </Badge>
                      ) : (
                        <Button 
                          size="sm"
                          onClick={() => gerarCertificadoPDF(missao)}
                          data-testid={`gerar-cert-${missao.missao_id}`}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Gerar PDF
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CertificadosPage;
